<template>
  <div
    class="relative min-h-fill-available mt-neg-header-candle pointer-events-none">
    <div
      class="relative sticky clamp-top-0-8 h-fill-available pt-header-candle pointer-events-none"
      :style="{
        '--d': `${radius * 2}px`,
        '--r': `${radius}px`,
        '--card-width': `${cardWidth}px`,
        '--card-height': `${cardHeight}px`,
        '--bg-offset': '60px',
      }">
      <div
        class="relative h-full pointer-events-none overflow-hidden py-1500px my--1500px"
        :style="{
          '--card-wrap-height': `${cardWrapHeight}px`,
        }">
        <div class="relative z-1">
          <slot name="header" />
        </div>
        <div
          ref="scrollElRef"
          class="relative w-full h-[--card-wrap-height] shrink-0 clamp-mt-18-28 pointer-events-auto">
          <AIconButton
            icon="arrow-left"
            size="sm"
            ariaLabel="Go to previous"
            :disabled="!showLeftArrow"
            class="!absolute top-50% left-0 translate-y--50% clamp-mx-3-5 z-100"
            @click="scrollCarousel('left')" />
          <AIconButton
            icon="arrow-right"
            size="sm"
            ariaLabel="Go to next"
            :disabled="!showRightArrow"
            class="!absolute top-50% right-0 translate-y--50% clamp-mx-3-5 z-100"
            @click="scrollCarousel('right')" />
          <div
            :class="[
              'circle-bg pointer-events-none',
              `before-w-[--d] before-h-[--d] before-content-[''] before-absolute before-clamp-top-6-6 before-ml:top-0 before-left-50% before-translate-x--50% before-mt-[--mt]`,
            ]"
            :style="{ '--mt': `calc(var(--bg-offset) * -1)` }" />
          <div
            class="w-[--d] h-[--d] absolute left-50% top-0 translate-x-[-50%] origin-center-center rotate-[--rotate] transition-transform-300 ease-out pointer-events-none"
            :style="{ '--rotate': angleCircleOffsetRad }">
            <template v-for="(card, index) in cards" :key="card._uid">
              <div
                class="absolute left-50% translate-x-[-50%] block w-[--card-width] h-[--r] clamp-px-2-20 tp:clamp-px-4-12 ds:clamp-px-2-8 origin-bottom-center rotate-[--rotate]"
                :style="{
                  '--rotate': `${round(
                    angleCardOffsetFactor * (index - config.indexOffset),
                    2
                  )}rad`,
                }">
                <div
                  class="relative w-full h-auto effect-hover-tilt-parent pointer-events-auto">
                  <SbElementSpotlightCarouselCard
                    :blok="card"
                    :index="index"
                    :total="cards.length"
                    :layout="layout"
                    :sizes="[
                      { bpMin: 'ds', vw: 20 },
                      { bpMin: 'tp', vw: 33 },
                      { bpMin: '0', vw: 50 },
                    ]" />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ElementSpotlightCardStoryblok,
  ModuleSpotlightStoryblok,
} from "@/types";
import { useWindowSize, useSwipe, UseSwipeDirection } from "@vueuse/core";
import { getBreakpoint } from "@/../uno-preset/preset-theme";

interface Props {
  cards: ElementSpotlightCardStoryblok[];
  layout: ModuleSpotlightStoryblok["layout"];
}

const props = defineProps<Props>();

// consts
const maskWidth = 387;
const maskHeight = 445;
const maskAspect = maskHeight / maskWidth;

const min = (a: number, b: number) => (a < b ? a : b);
const round = (n: number, decimals: number) => {
  const factor = 10 ** decimals;

  return Math.round(n * factor) / factor;
};

const configs = [
  {
    minVw: getBreakpoint("ds"),
    cardsVisibleCount: 5,
    radiusScale: 1,
    indexOffset: 2,
  },
  {
    minVw: getBreakpoint("tp"),
    cardsVisibleCount: 3,
    radiusScale: 1.5,
    indexOffset: 1,
  },
  {
    minVw: 0,
    cardsVisibleCount: 1,
    radiusScale: 3,
    indexOffset: 0,
  },
];

// refs
const scrollElRef = ref<HTMLElement | null>(null);
const scrollProgress = ref(0);

// composables
const { width } = useWindowSize();

const swiper = useSwipe(scrollElRef, {
  onSwipeEnd: (e: TouchEvent, direction: UseSwipeDirection) => {
    if (direction === "left" || direction === "right") {
      if (direction === "left") {
        scrollCarousel("right");
      } else {
        scrollCarousel("left");
      }
    }
  },
});

// computed
const showLeftArrow = computed(() => scrollProgress.value > 0);
const showRightArrow = computed(() => {
  const epsilon = 0.01;
  return (
    scrollProgress.value < 1 - epsilon &&
    props.cards.length > config.value.cardsVisibleCount
  );
});

const config = computed(() => {
  const config = configs.find((config) => width.value >= config.minVw);
  if (!config) throw new Error("No config found");

  return config;
});

const radius = computed(() => width.value * config.value.radiusScale);
const halfScreen = computed(() => (width.value * config.value.radiusScale) / 2);

// calculate the radius of the visible part of the circle on the screen
const angleCardOffsetFactor = computed(() => {
  const c1 = radius.value;
  const a1 = halfScreen.value;
  const A1 = Math.asin(a1 / c1);
  const A2 = A1 * 2; // angle of the visible part of the circle

  return (A2 / config.value.cardsVisibleCount) * (1 / config.value.radiusScale);
});

// calculate the angle offset of the hidden cards
const angleCircleOffsetRad = computed(() => {
  const cardsHiddenCount = props.cards.length - config.value.cardsVisibleCount;

  if (props.cards.length < config.value.cardsVisibleCount) {
    // if there are less cards than visible cards, center the cards
    const cardsMissingCount =
      config.value.cardsVisibleCount - props.cards.length;

    return `calc((${angleCardOffsetFactor.value} * ${
      cardsMissingCount / 2
    }) * 1rad)`;
  }

  const offsetMin = 0;
  const offsetMax = angleCardOffsetFactor.value * cardsHiddenCount;

  // lerp between offsetMin and offsetMax
  return `calc(((1 - ${scrollProgress.value}) * ${offsetMin} + ${scrollProgress.value} * ${offsetMax}) * -1rad)`;
});

// calculate the width of each card
const cardWidth = computed(() => {
  const c3 = radius.value;
  const A3 = angleCardOffsetFactor.value;
  const a3 = Math.sin(A3) * c3;

  return min(a3, width.value - 64); // make sure it's not wider than the screen minus some padding
});

const cardHeight = computed(() => cardWidth.value * maskAspect);

const cardWrapHeight = computed(() => {
  const c4 = radius.value * config.value.radiusScale;
  const a4 = halfScreen.value;
  const b4 = Math.sqrt(c4 * c4 - a4 * a4);

  const spaceBelowCenterCard = c4 - b4;

  return spaceBelowCenterCard + cardHeight.value;
});

// methods
const scrollCarousel = (direction: "left" | "right") => {
  const scrollDistance =
    1 / (props.cards.length - config.value.cardsVisibleCount);

  // round to nearest valid position using a small epsilon value
  const epsilon = 0.0001;
  if (Math.abs(scrollProgress.value % scrollDistance) < epsilon) {
    scrollProgress.value =
      Math.floor(scrollProgress.value / scrollDistance) * scrollDistance;
  }

  if (direction === "left") {
    scrollProgress.value = Math.max(0, scrollProgress.value - scrollDistance);
  } else {
    // for 'right' direction, calculate exact position
    const targetPosition = scrollProgress.value + scrollDistance;
    scrollProgress.value = Math.min(
      1 - epsilon, //prevent overshooting
      targetPosition
    );
  }
};
</script>

<style lang="scss" scoped>
.circle-bg {
  &:before {
    background: linear-gradient(var(--section-bg-color) 50%, transparent 50%);
    mask: radial-gradient(
      transparent calc(var(--r) - var(--card-height) - var(--bg-offset) + 16px),
      #000 calc(var(--r) - var(--card-height) - var(--bg-offset) + 16px),
      #000 var(--r),
      transparent var(--r)
    );
    filter: brightness(0.95) grayscale(0.05);
  }
}
</style>
